import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CarouselSlide, carouselSettings } from '../data/carouselSlides';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CarouselProps {
  slides: CarouselSlide[];
}

const Carousel = ({ slides }: CarouselProps) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-play functionality
  const startAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, carouselSettings.autoPlayInterval);
  }, [slides.length]);

  // Stop auto-play
  const stopAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  };

  // Initialize auto-play
  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [startAutoPlay]);

  // Pause on hover
  useEffect(() => {
    if (carouselSettings.pauseOnHover && isHovered) {
      stopAutoPlay();
    } else if (!isHovered) {
      startAutoPlay();
    }
  }, [isHovered, startAutoPlay]);

  // Navigate to specific slide
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    startAutoPlay(); // Restart auto-play timer
  };

  // Previous slide
  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    startAutoPlay();
  };

  // Next slide
  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
    startAutoPlay();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Touch/Swipe support
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swiped left
      goToNext();
    }
    if (touchStart - touchEnd < -75) {
      // Swiped right
      goToPrevious();
    }
  };

  return (
    <div
      className="relative w-full h-[400px] md:h-[500px] overflow-hidden bg-gray-100"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides Container */}
      <div className="relative h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-500 ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
          >
            {/* Desktop Layout: Split 60/40 */}
            <div className="hidden md:grid md:grid-cols-[60%_40%] h-full">
              {/* Image Section */}
              <div className={`relative bg-gradient-to-br ${slide.gradient}`}>
                <img
                  src={slide.image}
                  alt={slide.imageAlt}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Hide broken image, show gradient background
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>

              {/* Content Card */}
              <div className="flex items-start justify-center pt-8 md:pt-12 lg:pt-16 px-6 lg:px-10 xl:px-12 pb-6 bg-white overflow-y-auto">
                <div className="w-full max-w-lg">
                  <p className="text-sm uppercase tracking-widest text-gray-500 font-bold mb-3">
                    {slide.label}
                  </p>
                  <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 leading-tight font-serif">
                    {slide.heading}
                  </h2>
                  <p className="text-base lg:text-lg text-gray-600 mb-6 leading-relaxed font-sans">
                    {slide.description}
                  </p>
                  <Link
                    to={slide.buttonLink}
                    className="inline-block px-8 py-3 bg-blue-600 text-white rounded-lg font-bold text-base hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    {t(`carousel.${slide.buttonText}`, slide.buttonText)}
                  </Link>
                </div>
              </div>
            </div>

            {/* Mobile Layout: Overlay */}
            <div className="md:hidden relative h-full">
              {/* Image Section (Background) */}
              <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient}`}>
                <img
                  src={slide.image}
                  alt={slide.imageAlt}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                {/* Gradient Overlay for Text Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>

              {/* Content Card (Overlay) */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 text-white pb-16">
                <p className="text-xs uppercase tracking-widest text-gray-300 font-bold mb-2">
                  {slide.label}
                </p>
                <h2 className="text-2xl font-bold text-white mb-2 leading-tight font-serif drop-shadow-md">
                  {slide.heading}
                </h2>
                <p className="text-sm text-gray-200 mb-4 leading-relaxed font-sans line-clamp-2 drop-shadow-sm">
                  {slide.description}
                </p>
                <div>
                  <Link
                    to={slide.buttonLink}
                    className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all shadow-lg text-sm"
                  >
                    {t(`carousel.${slide.buttonText}`, slide.buttonText)}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-gray-800" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-gray-800" />
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${index === currentIndex
              ? 'bg-blue-600 w-8'
              : 'bg-white/70 hover:bg-white'
              }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
